import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useAuth } from '../../../auth/components/AuthProvider';
import * as AdminCampanasService from '../../services/adminCampanas.service';
import ModalConfirm from './ModalConfirm';
import AdminSolicitudesCampana from '../AdminSolicitudesCampana';

const STATUS_OPTIONS = [
  { value: 'activa_pausada', label: 'Activas + Pausadas' },
  { value: 'activa', label: 'Activas' },
  { value: 'pausada', label: 'Pausadas' },
  { value: 'eliminada', label: 'Eliminadas' },
  { value: 'todas', label: 'Todas' },
];

function formatDate(val) {
  if (!val) return '—';
  if (val?.toDate) return val.toDate().toLocaleString('es-CL', { dateStyle: 'short', timeStyle: 'short' });
  if (val?.seconds) return new Date(val.seconds * 1000).toLocaleString('es-CL', { dateStyle: 'short', timeStyle: 'short' });
  return '—';
}

function formatVigencia(c) {
  const ini = c.fechaInicio?.toDate?.() ?? (c.fechaInicio?.seconds ? new Date(c.fechaInicio.seconds * 1000) : null);
  const fin = c.fechaFin?.toDate?.() ?? (c.fechaFin?.seconds ? new Date(c.fechaFin.seconds * 1000) : null);
  if (!ini && !fin) return '—';
  const a = ini ? ini.toLocaleDateString('es-CL') : '?';
  const b = fin ? fin.toLocaleDateString('es-CL') : '?';
  return `${a} – ${b}`;
}

const TABS = [
  { id: 'publicadas', label: 'Campañas publicadas' },
  { id: 'solicitudes', label: 'Solicitudes a validar' },
];

export default function AdminCampanas() {
  const { user } = useAuth();
  const [mainTab, setMainTab] = useState('publicadas');
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('activa_pausada');
  const [search, setSearch] = useState('');
  const [searchDebounced, setSearchDebounced] = useState('');
  const [empresaFilter, setEmpresaFilter] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setSearchDebounced(search), 300);
    return () => clearTimeout(t);
  }, [search]);
  const [editItem, setEditItem] = useState(null);
  const [deleteItem, setDeleteItem] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await AdminCampanasService.fetchCampanas({ statusFilter });
      setList(data);
    } catch (e) {
      console.error(e);
      setList([]);
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    if (mainTab !== 'publicadas') return;
    load();
  }, [load, mainTab]);

  const empresas = useMemo(() => {
    const set = new Set(list.map((c) => c._displayEmpresa).filter(Boolean));
    return Array.from(set).sort((a, b) => String(a).localeCompare(b));
  }, [list]);

  const filtered = useMemo(() => {
    let out = list;
    const q = searchDebounced.trim().toLowerCase();
    if (q) {
      out = out.filter((c) => (c._displayTitle && c._displayTitle.toLowerCase().includes(q)) || (c._displayEmpresa && c._displayEmpresa.toLowerCase().includes(q)));
    }
    if (empresaFilter) out = out.filter((c) => c._displayEmpresa === empresaFilter);
    return out;
  }, [list, searchDebounced, empresaFilter]);

  const handlePause = async (item) => {
    if (!item?.id) return;
    setSaving(true);
    try {
      await AdminCampanasService.toggleCampanaPause(item.id, item._status, user?.uid);
      await load();
    } catch (e) {
      console.error(e);
      alert('Error al cambiar estado');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deleteItem?.id) return;
    setSaving(true);
    try {
      await AdminCampanasService.softDeleteCampana(deleteItem.id, user?.uid);
      setDeleteItem(null);
      await load();
    } catch (e) {
      console.error(e);
      alert('Error al eliminar');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveEdit = async (patch) => {
    if (!editItem?.id) return;
    setSaving(true);
    try {
      await AdminCampanasService.updateCampana(editItem.id, patch);
      setEditItem(null);
      await load();
    } catch (e) {
      console.error(e);
      alert('Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
        <h1 className="text-2xl font-bold text-gray-900">Campañas</h1>
        {mainTab === 'publicadas' && (
          <span className="text-sm text-gray-500">Total en vista: {filtered.length}</span>
        )}
      </div>

      <div className="flex gap-1 p-1 bg-gray-100 rounded-xl w-fit mb-6">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setMainTab(t.id)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
              mainTab === t.id ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {mainTab === 'solicitudes' && (
        <div className="mb-2">
          <p className="text-sm text-gray-600 max-w-3xl">
            Desde el detalle puedes <strong>editar</strong> título, fechas, presupuesto y descripción, <strong>dejar comentario</strong> a la empresa
            (en revisión) y <strong>activar</strong> para publicar. Lista con filtro &quot;Todos&quot; por defecto para ver también en revisión.
          </p>
        </div>
      )}

      {mainTab === 'solicitudes' ? (
        <AdminSolicitudesCampana embedded initialFilter="todos" />
      ) : (
        <>
      <div className="flex flex-wrap gap-4 mb-6">
        <input
          type="text"
          placeholder="Buscar por título o empresa..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-lg w-64 text-sm"
        />
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
        >
          {STATUS_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
        <select
          value={empresaFilter}
          onChange={(e) => setEmpresaFilter(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-lg text-sm min-w-[180px]"
        >
          <option value="">Todas las empresas</option>
          {empresas.map((e) => (
            <option key={e} value={e}>{e}</option>
          ))}
        </select>
      </div>
      {search.trim() && <p className="text-xs text-gray-500 mb-2">Búsqueda sobre los {list.length} cargados en esta página.</p>}

      {loading ? (
        <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-2 border-blue-200 border-t-blue-600" /></div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Campaña</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Empresa</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Estado</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Vigencia</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actualizado</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filtered.map((c) => (
                  <tr key={c.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-900">{c._displayTitle}</div>
                      {c.descripcion && <div className="text-xs text-gray-500 line-clamp-1">{c.descripcion}</div>}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">{c._displayEmpresa}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${
                        c._status === 'activa' ? 'bg-green-100 text-green-800' :
                        c._status === 'pausada' ? 'bg-amber-100 text-amber-800' : 'bg-gray-200 text-gray-700'
                      }`}>
                        {c._status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500">{formatVigencia(c)}</td>
                    <td className="px-4 py-3 text-sm text-gray-500">{formatDate(c.updatedAt ?? c.fechaCreacion)}</td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex gap-2 justify-end">
                        <button type="button" onClick={() => setEditItem(c)} className="text-blue-600 hover:underline text-sm" disabled={saving}>Editar</button>
                        {(c._status === 'activa' || c._status === 'pausada') && (
                          <button type="button" onClick={() => handlePause(c)} className="text-amber-600 hover:underline text-sm" disabled={saving}>
                            {c._status === 'activa' ? 'Pausar' : 'Reanudar'}
                          </button>
                        )}
                        {c._status !== 'eliminada' && (
                          <button type="button" onClick={() => setDeleteItem(c)} className="text-red-600 hover:underline text-sm" disabled={saving}>Eliminar</button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {filtered.length === 0 && <div className="p-8 text-center text-gray-500">No hay campañas que coincidan.</div>}
        </div>
      )}
        </>
      )}

      {editItem && (
        <EditCampanaModal
          item={editItem}
          onClose={() => setEditItem(null)}
          onSave={handleSaveEdit}
          saving={saving}
        />
      )}

      <ModalConfirm
        open={!!deleteItem}
        title="Eliminar campaña"
        message={deleteItem ? `¿Eliminar "${deleteItem._displayTitle}"? Se marcará como eliminada (soft delete).` : ''}
        confirmLabel="Eliminar"
        cancelLabel="Cancelar"
        danger
        onConfirm={handleDeleteConfirm}
        onCancel={() => setDeleteItem(null)}
      />
    </div>
  );
}

function EditCampanaModal({ item, onClose, onSave, saving }) {
  const [titulo, setTitulo] = useState(item?._displayTitle ?? '');
  const [descripcion, setDescripcion] = useState(item?.descripcion ?? '');
  const [status, setStatus] = useState(item?._status ?? 'activa');

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave({
      titulo: titulo.trim() || (item?.titulo ?? item?.nombre),
      descripcion: descripcion.trim() || null,
      status,
    });
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl max-w-lg w-full p-6" onClick={(e) => e.stopPropagation()}>
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Editar campaña</h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Título</label>
            <input type="text" value={titulo} onChange={(e) => setTitulo(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Descripción</label>
            <textarea value={descripcion} onChange={(e) => setDescripcion(e.target.value)} rows={3} className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Estado</label>
            <select value={status} onChange={(e) => setStatus(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg">
              <option value="activa">Activa</option>
              <option value="pausada">Pausada</option>
            </select>
          </div>
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50">Cancelar</button>
            <button type="submit" disabled={saving} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50">Guardar</button>
          </div>
        </form>
      </div>
    </div>
  );
}
